/* ══ THE APP'S OVERLAYS, IN THE BROWSER ══════════════════════════════════════
   🟥 THE WEB HAD NEVER DRAWN ANY OF THIS. A photo edited on the phone — a
      filter tint, a line of text, an emoji, a countdown, a poll, the loving
      indicator — arrived here as the bare picture. Everything the author had
      done to it was in the document the whole time; nothing read it. So a post
      genuinely looked like a different post depending on which client opened
      it, and a poll placed on a video could be answered by half the audience.

   ══ HOW THE PHONE'S COORDINATES ARE READ ═══════════════════════════════════
   A sticker is stored as an offset from the CENTRE of the picture, 40% of the
   way down, in the phone's own points, plus its own scale. The phone renders
   that at `scale: 1` full-screen and at `scale: 0.30` on a grid tile — the
   number is "how big is this surface compared with a phone screen".

   🟥 SO THE BROWSER HAS TO ASK THE SAME QUESTION OF A BOX WHOSE WIDTH IT DOES
      NOT KNOW UNTIL IT IS LAID OUT. It measures, and divides by the reference
      width below. That reproduces the phone's own limitation exactly — an
      overlay placed on a wide phone sits slightly differently on a narrow one —
      rather than inventing a second, better-behaved coordinate system that
      would put the sticker somewhere the author never put it.

   🟢 AND IT IS THE SAME RULES MODULE. Tallying, "has this person answered", and
      "is this the author" all come from services/vibeStickers, which the phone
      imports too — see the note at the top of it. */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  pollTally, sliderTally, myPollVote, mySliderValue, revealsResults, stickersForMedia, withMyAnswer, hasResponded,
  stickerResults, totalResponses, pollOptions,
} from '../services/vibeStickers';
import { answerSticker } from '../services/vibes';

/* 🟥 A PHONE'S WIDTH IN POINTS, and it has to be a constant because the
   document does not record the width of the screen the sticker was placed on.
   390 is the modern iPhone; the phone's own renderer makes the same assumption
   implicitly by treating stored offsets as points. */
export const REF_W = 390;

/* ── measuring the box the overlay sits in ────────────────────────────────── */

/**
 * 🟥 ResizeObserver, NOT a window resize listener. The reel pane is draggable
 *    and the feed card reflows when the right pane opens, neither of which
 *    resizes the window — the overlay would keep the width it was first laid
 *    out at and every sticker would sit in the wrong place.
 */
export function useBoxScale(ref) {
  const [w, setW] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const read = () => setW(el.clientWidth || 0);
    read();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return w > 0 ? w / REF_W : 0;
}

/* ── the filter tint ──────────────────────────────────────────────────────── */

/**
 * 🟢 A WASH, NOT A BAKED FILTER — which is why one stored colour is enough and
 *    nothing has to be re-processed. `mix-blend-mode: overlay` is the same
 *    compositing the phone asks for, so the two agree without a lookup table.
 */
export function FilterTint({ post }) {
  const color = post?.filterColor;
  if (!color || color === 'transparent') return null;
  const opacity = Math.max(0, Math.min(1, (Number(post.filterIntensity) || 100) / 100));
  return (
    <span
      className="vb-ov-tint"
      aria-hidden="true"
      style={{ background: color, opacity, mixBlendMode: 'overlay' }}
    />
  );
}

/* ── the caption the author drew ON the picture ───────────────────────────── */

const FONT_PX = { small: 16, med: 24, big: 36 };

/** The phone stores either a keyword or a raw px number. */
export function overlayFontPx(size) {
  if (typeof size === 'number' && Number.isFinite(size)) return size;
  return FONT_PX[size] || FONT_PX.med;
}

/**
 * The five looks, as the phone actually draws them.
 *
 * 🟥 THEY WERE FIVE DIFFERENT LOOKS WITH THE SAME FIVE NAMES. This was written
 *    from the names alone and every one of them was wrong: `neon` kept the text
 *    in the chosen colour under a weak double glow, where the phone paints the
 *    text WHITE and glows it in the chosen colour — which is what neon actually
 *    is, and why the web's looked muddy. `outline` was a four-way hard stroke
 *    where the phone uses one soft black shadow. `box` and `highlight` had
 *    invented paddings and radii. A post therefore read differently depending
 *    on which client opened it, which is the failure the whole overlay port
 *    exists to end — repeated inside it.
 *
 * 🟢 SO EVERY NUMBER BELOW COMES FROM `buildTextStyle` in the phone's
 *    VibesScreen: 10·s padding on a box, 8·s on a highlight, radius 10·s,
 *    lineHeight 1.32, vertical padding 0.16em, and neon's white core.
 *
 * 🟥 AND RN's ONE `textShadowRadius` IS NOT ONE CSS `text-shadow`. A native
 *    blur radius of 12 spreads far more softly than `0 0 12px` does; matching
 *    it takes stacked shadows, which is what the multi-stop glows below are.
 */
export function overlayTextStyle(post, scale) {
  const mode = post.overlayTextStyle || (post.overlayTextBg ? 'box' : 'shadow');
  const px = Math.max(6, overlayFontPx(post.overlayTextSize) * scale);
  const color = post.overlayTextColor || '#FFFFFF';
  const font = post.overlayTextFont && post.overlayTextFont !== 'System' ? post.overlayTextFont : undefined;
  const base = {
    fontSize: `${px}px`,
    color,
    textAlign: post.overlayTextAlign || 'center',
    fontFamily: font,
    fontWeight: 700,
    lineHeight: 1.32,
    padding: `${Math.max(2, px * 0.16)}px 0`,
  };
  const r = (n) => Math.max(2, n * scale);
  if (mode === 'box') {
    const pad = Math.round(10 * scale);
    return { ...base, background: 'rgba(0,0,0,0.55)', padding: `${Math.round(pad * 0.6)}px ${pad}px`, borderRadius: `${10 * scale}px` };
  }
  if (mode === 'highlight') {
    const pad = Math.round(8 * scale);
    return { ...base, color: '#000', background: color, padding: `${Math.round(pad * 0.6)}px ${pad}px`, borderRadius: `${10 * scale}px` };
  }
  if (mode === 'outline') {
    /* one soft black shadow, the phone's `textShadowRadius: max(2, 3s)` */
    return { ...base, textShadow: `0 0 ${r(3)}px #000, 0 0 ${r(3)}px #000` };
  }
  if (mode === 'neon') {
    /* 🟥 WHITE CORE, COLOURED GLOW — the phone sets `color: '#FFFFFF'` and
       shadows it in the chosen colour. Text left in its own colour under a
       faint halo is not neon, it is a blurry caption. */
    const g = Math.max(6, 12 * scale);
    return {
      ...base,
      color: '#FFFFFF',
      textShadow: `0 0 ${g * 0.28}px #FFF, 0 0 ${g * 0.7}px ${color}, 0 0 ${g * 1.5}px ${color}, 0 0 ${g * 2.6}px ${color}`,
    };
  }
  /* shadow — offset one point down, like the phone */
  return { ...base, textShadow: `0 ${Math.max(1, scale)}px ${r(4)}px rgba(0,0,0,0.5)` };
}

/* ── the interactive widgets ──────────────────────────────────────────────── */

/**
 * Optimistic, then authoritative — the phone's rule, for the phone's reason.
 *
 * 🟥 CLEARED BY AGREEMENT, NOT BY A TIMER. `pending` is dropped only once the
 *    stored answer equals it, so re-answering (Yes → No) still shows the new
 *    answer while the old one is still what the document says.
 */
function usePending(stored) {
  const [pending, setPending] = useState(null);
  useEffect(() => {
    if (pending !== null && stored === pending) setPending(null);
  }, [stored, pending]);
  return [pending !== null ? pending : stored, setPending];
}

function WebPoll({ sticker: rawSticker, post, me, scale, interactive }) {
  const uid = me?.uid;
  /* 😍📊 read THROUGH this tab's answers — whichever copy of the post this is */
  const sticker = useMemo(() => withMyAnswer(rawSticker, post?.id, uid) || rawSticker, [rawSticker, post?.id, uid]);
  const stored = myPollVote(sticker, uid);
  const [mine, setPending] = usePending(stored);
  const merged = useMemo(() => (
    mine === null || !uid ? sticker : { ...sticker, votes: { ...(sticker.votes || {}), [uid]: mine } }
  ), [sticker, uid, mine]);
  const tally = pollTally(merged);
  const labels = pollOptions(sticker);
  /* 😍📊 every viewer — the author too — sees it fresh until THEY answer */
  const reveal = hasResponded(merged, uid);

  const vote = (i) => {
    /* an answer is FINAL (the server refuses a second one) */
    if (!interactive || !uid || mine !== null) return;
    setPending(i);
    answerSticker(post.id, sticker.id, 'poll', i, uid).catch(() => {});
  };

  return (
    /* 🟥 A CLICK ON THE WIDGET IS NOT A CLICK ON THE PICTURE. In the feed the
       picture is a button that opens the post; a vote or a drag that ended in
       a click bubbled up to it and opened the post over the answer. */
    <div className="vb-ov-poll" onClick={(e) => e.stopPropagation()} style={{ width: `${220 * scale}px`, padding: `${20 * scale}px`, borderRadius: `${16 * scale}px` }}>
      <b style={{ fontSize: `${Math.max(6, 18 * scale)}px`, marginBottom: `${15 * scale}px` }}>
        {sticker.question || 'Ask a question...'}
      </b>
      <span className="vb-ov-pollrow" style={{ gap: `${10 * scale}px` }}>
        {[0, 1].map((i) => (
          <button
            key={i}
            type="button"
            className={`vb-ov-pollopt ${mine === i ? 'mine' : ''}`}
            style={{ borderRadius: `${10 * scale}px`, minHeight: `${40 * scale}px`, padding: `${10 * scale}px` }}
            disabled={!interactive || mine !== null}
            onClick={(e) => { e.stopPropagation(); vote(i); }}
          >
            {reveal && <i className="vb-ov-pollfill" style={{ width: `${tally.pct[i]}%` }} />}
            <em style={{ fontSize: `${Math.max(5, 14 * scale)}px` }}>{labels[i]}</em>
            {reveal && <u style={{ fontSize: `${Math.max(5, 12 * scale)}px` }}>{tally.pct[i]}%</u>}
          </button>
        ))}
      </span>
    </div>
  );
}

function WebSlider({ sticker: rawSticker, post, me, scale, interactive }) {
  const uid = me?.uid;
  const trackRef = useRef(null);
  /* 😍📊 read THROUGH this tab's answers — leave the post and come back, it is still answered */
  const sticker = useMemo(() => withMyAnswer(rawSticker, post?.id, uid) || rawSticker, [rawSticker, post?.id, uid]);
  const stored = mySliderValue(sticker, uid);
  const [mine, setPending] = usePending(stored);
  const merged = useMemo(() => (
    mine === null || !uid ? sticker : { ...sticker, values: { ...(sticker.values || {}), [uid]: mine } }
  ), [sticker, uid, mine]);
  const tally = sliderTally(merged);
  /* 😍📊 the face starts at the beginning for every viewer until THEY slide it */
  const reveal = hasResponded(merged, uid);

  /* 🟥 THE DRAG IS A POINTER CAPTURE, not mousemove on the document. The reel
     is inside a pane that scrolls and a video that swallows clicks; without
     capture the face stops following the finger the moment the pointer leaves
     the 40px track, which on a phone-sized browser window is immediately. */
  const [drag, setDrag] = useState(null);
  const at = drag !== null ? drag : (mine ?? 0);

  const ratioFrom = useCallback((clientX) => {
    const el = trackRef.current;
    if (!el) return 0;
    const r = el.getBoundingClientRect();
    if (r.width <= 0) return 0;
    return Math.min(1, Math.max(0, (clientX - r.left) / r.width));
  }, []);

  const down = (e) => {
    if (!interactive || !uid || mine !== null) return;   // an answer is FINAL
    e.stopPropagation(); e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setDrag(ratioFrom(e.clientX));
  };
  const move = (e) => { if (drag !== null) { e.stopPropagation(); setDrag(ratioFrom(e.clientX)); } };
  const up = (e) => {
    if (drag === null) return;
    e.stopPropagation();
    const v = ratioFrom(e.clientX);
    setDrag(null);
    setPending(v);
    answerSticker(post.id, sticker.id, 'slider', v, uid).catch(() => {});
  };

  return (
    <div className="vb-ov-slider" onClick={(e) => e.stopPropagation()} style={{ width: `${250 * scale}px`, padding: `${20 * scale}px`, borderRadius: `${16 * scale}px` }}>
      <b style={{ fontSize: `${Math.max(6, 18 * scale)}px`, marginBottom: `${20 * scale}px` }}>
        {sticker.question || 'Ask anything...'}
      </b>
      <span
        className="vb-ov-track" ref={trackRef}
        style={{ height: `${Math.max(4, 40 * scale)}px` }}
        onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up}
      >
        <i className="vb-ov-rail" style={{ height: `${Math.max(2, 8 * scale)}px`, borderRadius: `${4 * scale}px` }}>
          <u style={{ width: `${at * 100}%` }} />
        </i>
        {/* 🟢 THE CROWD'S ANSWER, drawn BESIDE yours and never on top of it — a
            widget that appears to move your answer for you is worse than one
            that shows no answer at all. */}
        {reveal && tally.total > 0 && (
          <em className="vb-ov-face crowd" style={{ left: `${tally.avg * 100}%`, fontSize: `${Math.max(8, 40 * scale)}px` }}>😍</em>
        )}
        <em className="vb-ov-face" style={{ left: `${at * 100}%`, fontSize: `${Math.max(8, 40 * scale)}px` }}>😍</em>
      </span>
      {/* 🟥 ONLY ONCE THERE IS SOMETHING TO READ. "0%" under an untouched slider
          is noise, and at a thumbnail's scale it is illegible noise. */}
      {reveal && tally.total > 0 && scale >= 0.5 && (
        <span className="vb-ov-avg" style={{ fontSize: `${Math.max(6, 13 * scale)}px` }}>
          {`${tally.avgPct}% · ${tally.total}`}
        </span>
      )}
    </div>
  );
}

/* ── one sticker ──────────────────────────────────────────────────────────── */

/**
 * Just the sticker's appearance, with no position and no editing chrome.
 *
 * 🟢 EXPORTED SO THE EDITOR DRAWS THE SAME THING THE VIEWER WILL. A composer
 *    that approximates what a sticker looks like is a composer that lies, and
 *    the two drift apart on the first tweak to either. The editor supplies its
 *    own bodies for the three kinds that have to be TYPED into — a poll, the
 *    loving indicator's question, a location — and reuses this for the other
 *    six, which look identical whether they are being placed or being read.
 */
export function StickerBody({ s, post, me, scale, interactive }) {
  const pill = (extra, children) => (
    <span className="vb-ov-pill" style={{ padding: `${10 * scale}px ${20 * scale}px`, borderRadius: `${20 * scale}px`, fontSize: `${Math.max(6, 24 * scale)}px`, ...extra }}>{children}</span>
  );

  let body = null;
  const kind = s.type || 'emoji';
  if (kind === 'emoji') {
    body = <span className="vb-ov-emoji" style={{ fontSize: `${Math.max(10, 70 * scale)}px` }}>{s.emoji}</span>;
  } else if (kind === 'time' || kind === 'date') {
    body = pill({}, s.locationText);
  } else if (kind === 'time_24') {
    body = (
      <span className="vb-ov-pill mono" style={{ padding: `${10 * scale}px ${20 * scale}px`, borderRadius: `${8 * scale}px`, fontSize: `${Math.max(8, 28 * scale)}px`, borderWidth: `${2 * scale}px` }}>
        {s.locationText}
      </span>
    );
  } else if (kind === 'clock') {
    body = (
      <span className="vb-ov-pill round" style={{ padding: `${10 * scale}px ${15 * scale}px`, borderRadius: `${30 * scale}px`, fontSize: `${Math.max(8, 22 * scale)}px` }}>
        <svg viewBox="0 0 24 24" width={Math.max(8, 24 * scale)} height={Math.max(8, 24 * scale)} fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" />
        </svg>
        {s.locationText}
      </span>
    );
  } else if (kind === 'location') {
    body = (
      <span className="vb-ov-pill round" style={{ padding: `${10 * scale}px ${20 * scale}px`, borderRadius: `${20 * scale}px`, fontSize: `${Math.max(6, 20 * scale)}px` }}>
        <svg viewBox="0 0 24 24" width={Math.max(6, 24 * scale)} height={Math.max(6, 24 * scale)} fill="currentColor" aria-hidden="true">
          <path d="M12 2a7 7 0 0 0-7 7c0 5 7 13 7 13s7-8 7-13a7 7 0 0 0-7-7Zm0 9.5A2.5 2.5 0 1 1 12 6a2.5 2.5 0 0 1 0 5.5Z" />
        </svg>
        {s.locationText || 'Location'}
      </span>
    );
  } else if (kind === 'gallery_image') {
    body = (
      <span className="vb-ov-photo" style={{ width: `${150 * scale}px`, height: `${150 * scale}px`, borderRadius: `${15 * scale}px`, borderWidth: `${3 * scale}px` }}>
        <img src={s.imageUrl} alt="" />
      </span>
    );
  } else if (kind === 'poll') {
    body = <WebPoll sticker={s} post={post} me={me} scale={scale} interactive={interactive} />;
  } else if (kind === 'slider') {
    body = <WebSlider sticker={s} post={post} me={me} scale={scale} interactive={interactive} />;
  }
  return body;
}

function Sticker(props) {
  const { s, scale } = props;
  /* 🟥 RENDERED AS AN ELEMENT, NOT CALLED AS A FUNCTION. `StickerBody` returns
     a poll or a loving indicator for two of the nine kinds, and both of those
     hold hooks; calling it inline would make those hooks belong to THIS
     component, so their state would be shared by every sticker on the picture. */
  return (
    <span
      className="vb-ov-item"
      style={{ transform: `translate(-50%, -50%) translate(${s.x * scale}px, ${s.y * scale}px) scale(${stickerScale(s)})` }}
    >
      <StickerBody {...props} />
    </span>
  );
}

/** The phone clamps a pinch to this range; a stored value outside it is a bug
 *  somewhere else that must not become a full-screen emoji here. */
export function stickerScale(s) {
  return Math.min(5, Math.max(0.35, Number(s?.scale) || 1));
}

/* ── the layer ────────────────────────────────────────────────────────────── */

/**
 * Everything the author drew, over one picture.
 *
 * 🟥 `pointer-events: none` ON THE LAYER, re-enabled on the two widgets that
 *    need it. The overlay covers the whole picture; without this, clicking a
 *    video to pause it would be swallowed by an invisible sheet of stickers.
 */
export function Overlay({ post, me, interactive = false, className = '', mediaIndex = 0 }) {
  const ref = useRef(null);
  const scale = useBoxScale(ref);
  /* 🖼 ONLY THIS PICTURE'S STICKERS (services/vibeStickers stickersForMedia).
     The caption and the tint are the first picture's — the post is stamped
     from it — so they are drawn there and nowhere else. */
  const first = (Number(mediaIndex) || 0) === 0;
  const stickers = stickersForMedia(post, mediaIndex);
  const hasText = first && !!post?.overlayText;
  const has = stickers.length > 0 || hasText || (first && post?.filterColor && post.filterColor !== 'transparent');

  return (
    <span className={`vb-ov ${className}`} ref={ref} aria-hidden={has ? undefined : 'true'}>
      {first && <FilterTint post={post} />}
      {scale > 0 && stickers.map((s, i) => (
        <Sticker key={s?.id || i} s={s} post={post} me={me} scale={scale} interactive={interactive} />
      ))}
      {scale > 0 && hasText && (
        <span
          className={`vb-ov-text a-${post.overlayTextAlign || 'center'}`}
          style={{
            transform: `translate(-50%, 0) translate(${(post.overlayTextX || 0) * scale}px, ${(post.overlayTextY || 0) * scale}px)`,
            ...overlayTextStyle(post, scale),
          }}
        >
          {post.overlayText}
        </span>
      )}
    </span>
  );
}

/* ── the author's results ─────────────────────────────────────────────────── */

/** Is there anything for this viewer to be shown the results of? */
export function hasAuthorResults(post, uid) {
  return !!post && !!uid && post.userId === uid && stickerResults(post).length > 0;
}

/**
 * 📊 THE AUTHOR'S OWN NUMBERS — the browser's twin of ReelMeta's `ReelResults`.
 *
 * 🟢 IT EXPANDS IN PLACE rather than opening a dialog, for the same reason the
 *    phone's does: a reel is already a surface inside a surface, and the panel
 *    has to be readable without leaving the clip that is playing.
 */
export function ReelResults({ post, className = '' }) {
  const [open, setOpen] = useState(false);
  const rows = useMemo(() => stickerResults(post), [post]);
  const total = useMemo(() => totalResponses(post), [post]);
  if (!rows.length) return null;

  if (!open) {
    return (
      <button type="button" className={`vb-res-pill ${className}`} onClick={() => setOpen(true)}>
        <svg viewBox="0 0 24 24" width="13" height="13" fill="currentColor" aria-hidden="true">
          <path d="M4 20h3V10H4v10Zm6.5 0h3V4h-3v16ZM17 20h3v-7h-3v7Z" />
        </svg>
        Results
        <i>· {total}</i>
      </button>
    );
  }

  return (
    <div className={`vb-res-panel ${className}`}>
      <header>
        <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor" aria-hidden="true">
          <path d="M4 20h3V10H4v10Zm6.5 0h3V4h-3v16ZM17 20h3v-7h-3v7Z" />
        </svg>
        Your results
      </header>
      {rows.map((r) => (
        <section key={r.id}>
          <b>{r.question}</b>
          <i>{r.total === 0 ? 'No answers yet' : `${r.total} answer${r.total === 1 ? '' : 's'}`}</i>
          {r.kind === 'poll' ? r.options.map((o, i) => (
            /* 🟢 A BAR AND THE RAW COUNT. "100%" of one vote and "100%" of four
               hundred are the same string, and the author is here to tell those
               two apart. */
            <span className="vb-res-row" key={`${r.id}-${i}`}>
              <span className="vb-res-lab"><em>{o.label}</em><u>{o.pct}% · {o.count}</u></span>
              <span className="vb-res-bar"><i style={{ width: `${o.pct}%` }} /></span>
            </span>
          )) : (
            <span className="vb-res-row">
              <span className="vb-res-lab"><em>Average</em><u>{r.avgPct}%</u></span>
              <span className="vb-res-bar love"><i style={{ width: `${r.avgPct}%` }} /></span>
            </span>
          )}
        </section>
      ))}
      <button type="button" className="vb-res-done" onClick={() => setOpen(false)}>Done</button>
    </div>
  );
}
